import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _delete, _get, _getBlob, _patch, _post, _put } from "../../Services/baseApi";
import { errorHandler, successToast } from "../../Utils/toast";
import {
  downloadBlobFile,
  getFilenameFromDisposition,
} from "../../Utils/downloadFile";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { handleAsyncState } from "./../common/helpers";

import {
  USER_ADD_URL,
  USER_LIST_URL,
  USER_EXPORT_URL,
  USER_DETAILS_URL,
  USER_UPDATE_URL,
  USER_DELETE_URL,

  // user payment
  USER_PAYMENT_DETAILS_URL,
  USER_PAYMENT_ADD_URL,
  USER_PAYMENT_UPDATE_URL,
  UPDATE_USER_PASSWORD,
} from "../../Services/endpoints";

const initialState = {
  isLoading: false,
  usersList: {
    findUsers: [],
    limit: 0,
    page: 0,
    totalCount: 0,
  },
  userDetails: {},
  userDocDetails: {},
  userPaymentDetails: {},
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    userDetailsClean: (state) => {
      state.userDetails = {};
    },
    userDocDetailsClean: (state) => {
      state.userDocDetails = {};
    },
    userPaymentDetailsClean: (state) => {
      state.userPaymentDetails = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsersThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUsersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.usersList = action.payload;
      })
      .addCase(getUsersThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(addUsersThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addUsersThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addUsersThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(updateUsersThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUsersThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateUsersThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(deleteUsersThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteUsersThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteUsersThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(getUsersDetailsThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUsersDetailsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userDetails = action.payload;
      })
      .addCase(getUsersDetailsThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(getUsersPaymentDetailsThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUsersPaymentDetailsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userPaymentDetails = action.payload;
      })
      .addCase(getUsersPaymentDetailsThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(addUsersPaymentThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addUsersPaymentThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addUsersPaymentThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(updateUsersPaymentThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUsersPaymentThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateUsersPaymentThunk.rejected, (state) => {
        state.isLoading = false;
      });

    handleAsyncState(builder, updateUserPassword);
  },
});

export const {
  userDetailsClean,
  userDocDetailsClean,
  userPaymentDetailsClean,
} = usersSlice.actions;
// export default usersSlice.reducer;

// Define the reducer export with persist data
export default persistReducer(
  {
    key: "users/persist",
    storage,
    whitelist: [],
  },
  usersSlice.reducer
);

export const getUsersThunk = createAsyncThunk(
  "usersSlice/getUsersThunk",
  async (payload) => {
    try {
      const response = await _get(USER_LIST_URL, {
        type: payload?.type || undefined,
        page: payload?.page || undefined,
        limit: payload?.limit || undefined,
        name: payload?.name || undefined,
        key: payload?.key ? payload?.key : undefined,
        keyOrder:
          payload?.key && payload?.keyOrder ? payload?.keyOrder : undefined,
        agencyId: payload?.agencyId || undefined,
        roleId: payload?.roleId || undefined,
        roleName: payload.roleName || undefined,
        isRouteAssignd: payload?.routestatus || undefined,
      });
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const exportUsersThunk = createAsyncThunk(
  "usersSlice/exportUsersThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _getBlob(USER_EXPORT_URL, {
        name: payload?.name || undefined,
        type: payload?.type || undefined,
      });
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        "admin-users-export.xlsx"
      );
      downloadBlobFile(response.data, filename);
      successToast("Admin users exported successfully");
      return true;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getUsersDetailsThunk = createAsyncThunk(
  "usersSlice/getUsersDetailsThunk",
  async (payload) => {
    try {
      const response = await _get(`${USER_DETAILS_URL}/${payload?.id}`);
      // payload.handleFun(response?.data?.data);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const addUsersThunk = createAsyncThunk(
  "usersSlice/addUsersThunk",
  async (payload) => {
    try {
      const response = await _post(USER_ADD_URL, {
        name: payload?.name,
        phoneNumber:
          payload?.phoneNumber != null
            ? String(payload.phoneNumber).trim()
            : undefined,
        emailId: payload?.emailId || undefined,
        roleId: payload?.roleId,
        file: payload?.file || undefined,
        fileType:
          payload?.file && payload?.fileType ? payload?.fileType : undefined,
        userAgencyId: payload?.userAgencyId || undefined,
        status: payload?.status,
        locations: payload?.locations,
      });
      payload.handleFun(response?.data?.data);
      successToast(response?.data?.message);
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const updateUsersThunk = createAsyncThunk(
  "usersSlice/updateUsersThunk",
  async (payload) => {
    try {
      const response = await _patch(`${USER_UPDATE_URL}/${payload.id}`, {
        id: payload.id,
        name: payload?.name,
        phoneNumber:
          payload?.phoneNumber != null
            ? String(payload.phoneNumber).trim()
            : undefined,
        emailId: payload?.emailId || "",
        roleId: payload?.roleId,
        file: payload?.file,
        fileType: payload?.file && payload?.fileType,
        userAgencyId: payload?.userAgencyId,
        status: payload?.status,
        type: payload?.type,
        locations: payload?.locations,
      });
      payload.handleFun();
      successToast(response?.data?.message);
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const updateUserPassword = createAsyncThunk(
  "usersSlice/updateUserPassword",
  async (payload) => {
    try {
      const response = await _put(UPDATE_USER_PASSWORD, {
        id: payload.id,
        oldPassword: payload?.oldPassword,
        newPassword: payload?.newPassword,
      });
      payload.handleFun();
      successToast(response?.data?.message);
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const deleteUsersThunk = createAsyncThunk(
  "usersSlice/deleteUsersThunk",
  async (payload) => {
    try {
      const response = await _delete(`${USER_DELETE_URL}/${payload?.id}`);
      payload.handleFun();
      successToast(response?.data?.message);
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const getUsersPaymentDetailsThunk = createAsyncThunk(
  "usersSlice/getUsersPaymentDetailsThunk",
  async (payload) => {
    try {
      const response = await _get(`${USER_PAYMENT_DETAILS_URL}/${payload?.id}`);
      payload.handleFun(response?.data?.data);
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const addUsersPaymentThunk = createAsyncThunk(
  "usersSlice/addUsersPaymentThunk",
  async (payload) => {
    try {
      const response = await _post(USER_PAYMENT_ADD_URL, {
        userId: payload?.userId,
        bankAccountNumber: payload?.bankAccountNumber,
        bankIFSCCode: payload?.bankIFSCCode,
        bankName: payload?.bankName,
        salaryAmount: payload?.salaryAmount,
        paymentType: payload?.paymentType,
        advanceAmount: payload?.advanceAmount,
      });
      payload.handleFun(response?.data?.data);
      successToast(response?.data?.message);
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const updateUsersPaymentThunk = createAsyncThunk(
  "usersSlice/updateUsersPaymentThunk",
  async (payload) => {
    try {
      const response = await _put(USER_PAYMENT_UPDATE_URL, {
        id: payload?.id,
        bankAccountNumber: payload?.bankAccountNumber,
        bankIFSCCode: payload?.bankIFSCCode,
        bankName: payload?.bankName,
        salaryAmount: payload?.salaryAmount,
        paymentType: payload?.paymentType,
        userId: payload?.userId,
        advanceAmount: payload?.advanceAmount,
      });
      payload.handleFun();
      successToast(response?.data?.message);
      return response?.data?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);
